import type { ChipProps } from '@mui/material';

import type { CoreConfigurationStatus, CoreExpectedWebhookStatus } from './coreConfigurationApi';

export const REACTIVATABLE_CORE_STATUSES = ['failed', 'disabled'] as const;

export type WebhookChipPresentation = {
  label: 'Configured' | 'Failed' | 'Disabled' | 'Not configured';
  color: Extract<ChipProps['color'], 'success' | 'error' | 'warning'>;
};

export const getWebhookChipPresentation = (
  webhook: Pick<CoreExpectedWebhookStatus, 'configured' | 'coreStatus'>,
): WebhookChipPresentation => {
  if (!webhook.configured) return { label: 'Not configured', color: 'error' };
  if (webhook.coreStatus === 'failed') return { label: 'Failed', color: 'error' };
  if (webhook.coreStatus === 'disabled') return { label: 'Disabled', color: 'warning' };
  return { label: 'Configured', color: 'success' };
};

const isReactivatableCoreStatus = (
  status: string | null | undefined,
): status is (typeof REACTIVATABLE_CORE_STATUSES)[number] => status === 'failed' || status === 'disabled';

export const isReactivatableWebhook = (webhook: Pick<CoreExpectedWebhookStatus, 'configured' | 'coreStatus'>): boolean =>
  webhook.configured && isReactivatableCoreStatus(webhook.coreStatus);

export const canProvisionToken = (status: Pick<CoreConfigurationStatus, 'tokenValid' | 'envTokenInUse'>): boolean =>
  !status.tokenValid && !status.envTokenInUse;

export const canProvisionWebhooks = (status: {
  tokenValid: boolean;
  webhooks: ReadonlyArray<Pick<CoreExpectedWebhookStatus, 'configured'>>;
}): boolean => status.tokenValid && status.webhooks.some(webhook => !webhook.configured);

export const canReactivateWebhooks = (status: {
  tokenValid: boolean;
  webhooks: ReadonlyArray<Pick<CoreExpectedWebhookStatus, 'configured' | 'coreStatus'>>;
}): boolean => status.tokenValid && status.webhooks.some(isReactivatableWebhook);

export const formatCoreConfigurationCount = (value: number | null | undefined): string | null =>
  typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : null;
